import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../services/api';
import { fetchWithCache } from '../services/cache';
import LazyImage from '../components/LazyImage';
import { RotatingLines } from 'react-loader-spinner';

const PageHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.secondary};
  color: white;
  padding: 4rem 2rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 3rem;
`;

const Section = styled.section`
  padding: 5rem 2rem;
  background-color: ${({ $alt, theme }) => $alt ? '#f9f9f9' : 'white'};
`;

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.large};
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ContentBox = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    order: ${({ $reverse }) => $reverse ? 1 : 2};
  }
`;

const ImageBox = styled.div`
  height: 350px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.large};
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    order: ${({ $reverse }) => $reverse ? 2 : 1};
  }
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 1.5rem;
  font-size: 2rem;
`;

const Text = styled.div`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  white-space: pre-wrap;
`;

const About = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchWithCache('aboutData', () => api.get('/about'), setData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading && !data) return (
    <div style={{display:'flex', justifyContent:'center', marginTop:'50px', minHeight:'100vh'}}>
      <RotatingLines strokeColor="grey" width="50" />
    </div>
  );

  if (!data) return <div>Error loading data</div>;

  const { bio, mission, vision, goals, otherSections } = data;

  const renderSection = (item, index, alt) => (
    <Section key={index} $alt={alt}>
      <Container>
        <Grid>
           <ImageBox $reverse={index % 2 !== 0}>
             {item.image && <LazyImage src={item.image} alt={item.title} objectFit="contain" />}
           </ImageBox>
           <ContentBox $reverse={index % 2 !== 0}>
             <SectionTitle>{item.title}</SectionTitle>
             <Text>{item.content}</Text>
           </ContentBox>
        </Grid>
      </Container>
    </Section>
  );

  return (
    <div>
      <PageHeader>
        <Title>About Us</Title>
      </PageHeader>
      
      {renderSection(bio, 0, false)}
      {renderSection(mission, 1, true)}
      {renderSection(vision, 2, false)}
      {renderSection(goals, 3, true)}
      
      {otherSections && otherSections.map((section, idx) => (
        renderSection(section, idx + 4, (idx + 4) % 2 !== 0)
      ))}
    </div>
  );
};

export default About;
